import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import loadOrderApprovals from "@insite/client-framework/Store/Data/OrderApprovals/Handlers/LoadOrderApprovals";
import { getOrderApprovalsDataView } from "@insite/client-framework/Store/Data/OrderApprovals/OrderApprovalsSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Phone from "@insite/mobius/Icons/Phone";
import User from "@tCentro/Icons/User";
import Link from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries, get } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import Megaphone from "@tCentro/Icons/Megaphone";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import { getUtmParamsFromUrl } from "@tCentro/utilities/utmParameters";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
import styled from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    link = "link",
    includeAccountMenu = "includeAccountMenu",
    mobilePhoneNo = "mobilePhoneNo",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    getOrderApprovalsParameter: state.pages.orderApprovalList.getOrderApprovalsParameter,
    isAuthenticated: state.context?.session?.isAuthenticated && !state.context?.session?.isGuest,
    orderApprovalsDataView: getOrderApprovalsDataView(state, state.pages.orderApprovalList.getOrderApprovalsParameter),
    settings: getSettingsCollection(state),
    session: getSession(state),
    orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
    onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    loadOrderApprovals,
    setSignInOverlayIsOpen,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    mainContainer?: InjectableCss;
    phoneIconStyles?: IconPresentationProps;
    phoneClickAble?: InjectableCss;
    phoneNoStyles?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    phoneNoStyles: {
        css: css`
            color: ${getColor("primary")};
            line-height: 16px;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-family: ${TYPOGRAPHY.fontFamily.title};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            display: none;
                        }
                    `,
                ]);
            }}
        `,
    },
    phoneClickAble: {
        css: css`
            color: ${getColor("primary")};
            gap: 2px;
        `,
    },
    mainContainer: {
        css: css`
            display: flex;
        `,
    },
    phoneIconStyles: {
        size: 24,
        css: css`
            svg {
                fill: ${getColor("primary")};
                stroke: ${getColor("primary")};

                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                        css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                        css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                        css`
                            @media (min-width: 993px) and (max-width: 1079px) {
                                fill: ${get(theme, "colors.info.contrast")};
                                stroke: none;
                            }
                        `,
                    ]);
                }}
            }
        `,
    },

    titleClickable: {
        css: css`
            margin-left: 10px;
            gap: 4px;

            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        /* display: none; */
                    `,
                    css`
                        /* display: none; */
                    `,
                    css`
                        /* display: none; */
                    `,
                    null,
                ]);
            }}
        `,
    },
    titleIcon: {
        size: 24,
        css: css`
            svg {
                fill: ${getColor("primary")};
                stroke: ${getColor("primary")};

                height: 100%;
                width: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                        css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                        css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                        css`
                            @media (min-width: 993px) and (max-width: 1079px) {
                                fill: ${get(theme, "colors.info.contrast")};
                                stroke: none;
                            }
                        `,
                    ]);
                }}
            }
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 0;
            margin-top: 0;
            color: ${getColor("primary")};
            text-transform: capitalize;
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        @media (min-width: 993px) and (max-width: 1079px) {
                            display: none;
                        }
                    `,
                ]);
            }}
        `,
        // ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            text-transform: capitalize;
            padding: 10px 15px 10px 20px;
            color: ${COLORS.text.mineShaft};
            font-size: ${TYPOGRAPHY.size.small};
            width: 100%;

            &:hover {
                background: ${COLORS.border.base};
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.link]: LinkFieldValue;
        [fields.mobilePhoneNo]: string;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    isAuthenticated,
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    id,
    getOrderApprovalsParameter,
    orderApprovalsDataView,
    loadOrderApprovals,
    setSignInOverlayIsOpen,
    theme,
    settings,
    session,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
}: Props) => {
    const link = useGetLink(fields.link);
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    const signOutWrapper = {
        css: css`
            display: inline-flex;
            gap: 6px;
            flex-direction: ${isAuthenticated ? "row-reverse" : "row"};
            nav {
                ul {
                    width: 200px;
                    max-height: 260px;
                    overflow-y: scroll;
                    @media (max-width: 1079px) {
                        display: none !important;
                    }
                }
            }
        `,
    };

    if (fields.icon === "User") {
        icon = User;
    }

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }

    const [isOpen, setIsOpen] = useState(false);

    React.useEffect(() => {
        if (userName && !currentUserIsGuest && !orderApprovalsDataView.value && !orderApprovalsDataView.isLoading) {
            loadOrderApprovals(getOrderApprovalsParameter);
        }
    }, []);

    React.useEffect(() => {
        document.addEventListener("mouseup", (e: any) => {
            const shopCentroBtn = document.getElementById("my-account-flyout");

            if (isOpen && !shopCentroBtn?.contains(e.target)) {
                shopCentroBtn?.classList.remove("open-my-account-flyout");
                setIsOpen(false);
            }
        });
    });

    const onClick = () => {
        if (!link.url) {
            const myAccountBtn = document.getElementById("my-account-flyout");
            myAccountBtn?.classList.toggle("open-my-account-flyout");
            setIsOpen(true);
        }
    };

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const handleMyAccountHandler = () => {
        history.push("/MyAccount");
    };

    let signInStatusText = <span>Login / Register</span>;
    if (userName && !currentUserIsGuest) {
        signInStatusText = <span>My Account</span>;
    }

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    let childPageLinks = myAccountPageLink?.children;
    if (
        orderApprovalsDataView &&
        orderApprovalsDataView.value &&
        !orderApprovalsDataView.isLoading &&
        orderApprovalsDataView.pagination &&
        myAccountPageLink?.children
    ) {
        childPageLinks = myAccountPageLink?.children.map(pLink => {
            if (pLink.type === "OrderApprovalListPage") {
                const newLink = {
                    ...pLink,
                    title: `${pLink.title}   (${orderApprovalsDataView.pagination?.totalItemCount})`,
                };

                return newLink;
            }
            return pLink;
        });
    }

    const displayMyQuotes = (settings.customSettings as { enableRFQForPunchout?: boolean })?.enableRFQForPunchout;

    useEffect(() => {
        const utmParams = getUtmParamsFromUrl(window.location.href);
        // Only set UTM params in sessionStorage if they exist
        if (utmParams.optimizely_campaign) {
            sessionStorage.setItem("utm_campaign", utmParams.optimizely_campaign);
        }
        if (utmParams.optimizely_medium) {
            sessionStorage.setItem("utm_medium", utmParams.optimizely_medium);
        }
        if (utmParams.optimizely_source) {
            sessionStorage.setItem("utm_source", utmParams.optimizely_source);
        }
    }, []);

    // Filter out My Account menu items.url === "/MyAccount/taxonomy" based on roless is SME then hide Taxonomy link
    const hasSMERole = /\bCentro_SME\b/.test(session?.userRoles);
    if (!hasSMERole) {
        menuItems = menuItems?.filter(pageLink => pageLink.url !== "/MyAccount/taxonomy");
    }

    const hasSMEAdminRole = /\bSME-ADMIN\b/.test(session?.userRoles);
    const hasCentroEmployeeRole = /\bCentro-Employee\b/.test(session?.userRoles);

    // Only centro-connect-ADMIN can see /MyAccount/centro-connect-admin
    if (!hasSMEAdminRole) {
        menuItems = menuItems?.filter(pageLink => pageLink.url !== "/MyAccount/centro-connect-admin");
    }

    // centro-connect-ADMIN or Centro-Employee can see /MyAccount/centro-connect
    if (!hasSMEAdminRole && !hasCentroEmployeeRole) {
        menuItems = menuItems?.filter(pageLink => pageLink.url !== "/MyAccount/centro-connect");
    }

    // Sort to show centro-connect first, then centro-connect-admin
    if (menuItems) {
        menuItems = menuItems.sort((a, b) => {
            if (a.url === "/MyAccount/centro-connect") return -1;
            if (b.url === "/MyAccount/centro-connect") return 1;
            if (a.url === "/MyAccount/centro-connect-admin") return -1;
            if (b.url === "/MyAccount/centro-connect-admin") return 1;
            return 0;
        });
    }

    return (
        <StyledWrapper {...signOutWrapper}>
            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={
                        menuItems?.filter(
                            pageLink =>
                                !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink) &&
                                (!(isPunchOutSession && !displayMyQuotes) || pageLink.title !== "My Quotes"),
                        ) ?? []
                    }
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    menuTrigger={
                        <Clickable
                            {...styles.titleClickable}
                            data-test-selector="header_signInMyAccount"
                            onClick={handleMyAccountHandler}
                        >
                            {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                            {showLabel && (
                                <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                    My Account
                                </Typography>
                            )}
                        </Clickable>
                    }
                    cssOverrides={{
                        wrapper: css`
                            flex: 0 1 auto;
                            min-width: 0;
                        `,
                        menuItem: css`
                            a {
                                span {
                                    white-space: pre-wrap;
                                    color: ${COLORS.text.mineShaft};
                                }
                            }
                        `,
                    }}
                    data-test-selector="header_accountMenu"
                >
                    {userName && !currentUserIsGuest && (
                        <Clickable
                            {...styles.signOutClickable}
                            onClick={onSignOutHandler}
                            data-test-selector="headerSignOutLink"
                        >
                            {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                        </Clickable>
                    )}
                </Menu>
            )}
            <StyledWrapper {...styles.mainContainer}>
                <Clickable {...styles.phoneClickAble} href={`tel:${fields.mobilePhoneNo}`} data-test-selector="phoneNo">
                    <Icon src={Phone} {...styles.phoneIconStyles}></Icon>
                    <Typography as="span" {...styles.phoneNoStyles}>
                        {fields.mobilePhoneNo}
                    </Typography>
                </Clickable>
                {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                    <StyledWrapper>
                        <Clickable
                            {...styles.titleClickable}
                            onClick={onSignInHandler}
                            data-test-selector="header_signIn"
                        >
                            {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                            {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: block;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    text-align: center;
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.mobilePhoneNo,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
